import React, { useState, useMemo } from 'react';
import { Calculator, TrendingUp, TrendingDown, AlertCircle, Hammer, Wrench, HardHat } from 'lucide-react';

// SSOT wholesale defaults (mirrored from config.py)
const WHOLESALE_DEFAULTS = {
  arv_discount: 0.70,
  assignment_fee_pct: 0.05,
  assignment_fee_min: 5000,
  assignment_fee_max: 25000,
  repair_tiers: {
    cosmetic: 15000,
    moderate: 35000,
    gut: 75000,
  },
  verdict_thresholds: {
    strong_buy: 20000,
    buy: 10000,
    thin: 0,
  }
};

type RepairTier = 'cosmetic' | 'moderate' | 'gut';
type Verdict = 'strong_buy' | 'buy' | 'thin' | 'pass';

interface WholesalePanelProps {
  askingPrice?: number;
  estimatedARV?: number;  // From market_value or sale comps
  propertyAddress?: string;
}

const formatCurrency = (value: number | null | undefined): string => {
  if (value === null || value === undefined) return '$0';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(value);
};

const VerdictBadge: React.FC<{ verdict: Verdict; spread: number }> = ({ verdict, spread }) => {
  const configs: Record<Verdict, { bg: string; text: string; border: string; label: string }> = {
    strong_buy: { bg: 'bg-emerald-500/20', text: 'text-emerald-400', border: 'border-emerald-500/50', label: 'STRONG BUY' },
    buy: { bg: 'bg-green-500/20', text: 'text-green-400', border: 'border-green-500/50', label: 'BUY' },
    thin: { bg: 'bg-amber-500/20', text: 'text-amber-400', border: 'border-amber-500/50', label: 'THIN MARGIN' },
    pass: { bg: 'bg-red-500/20', text: 'text-red-400', border: 'border-red-500/50', label: 'PASS' },
  };

  const config = configs[verdict];

  return (
    <div className={`${config.bg} ${config.border} border rounded-lg px-4 py-3 text-center`}>
      <div className={`text-2xl font-bold ${config.text}`}>{config.label}</div>
      <div className="text-sm text-stone-700 mt-1">
        Spread: <span className={spread >= 0 ? 'text-green-400' : 'text-red-400'}>{formatCurrency(spread)}</span>
      </div>
    </div>
  );
};

const RepairTierButton: React.FC<{
  tier: RepairTier;
  selected: boolean;
  onClick: () => void;
  cost: number;
  icon: React.ReactNode;
  label: string;
  description: string;
}> = ({ tier: _tier, selected, onClick, cost, icon, label, description }) => (
  <button
    onClick={onClick}
    className={`
      flex-1 p-4 rounded-lg border-2 transition-all text-left
      ${selected
        ? 'bg-gold/10 border-gold text-ivory'
        : 'bg-graphite border-slate-dark text-stone-700 hover:border-zinc-500'
      }
    `}
  >
    <div className="flex items-center gap-2 mb-2">
      {icon}
      <span className="font-medium">{label}</span>
    </div>
    <div className={`text-lg font-mono ${selected ? 'text-gold' : 'text-stone-700'}`}>
      {formatCurrency(cost)}
    </div>
    <div className="text-sm text-zinc-500 mt-1">{description}</div>
  </button>
);

export const WholesalePanel: React.FC<WholesalePanelProps> = ({
  askingPrice: initialAskingPrice,
  estimatedARV,
  propertyAddress: _propertyAddress,
}) => {
  // EXPANSION: ARV from Sale Comps UI (Phase 51-04)
  // Add a "Estimate from Comps" button next to ARV input that calls backend
  // _estimate_arv_from_comps(). Display comp count, confidence level, and
  // price range. Allow user to accept or override the estimate.
  // See: docs/architecture/WHOLESALE_SYSTEM.md § Expansion Points

  // State for user inputs
  const [arv, setArv] = useState<number>(estimatedARV || 0);
  const [askingPrice, setAskingPrice] = useState<number>(initialAskingPrice || 0);
  const [repairTier, setRepairTier] = useState<RepairTier>('moderate');
  const [assignmentFeePct, setAssignmentFeePct] = useState<number>(WHOLESALE_DEFAULTS.assignment_fee_pct * 100);

  // Calculate MAO and related values
  const maoResult = useMemo(() => {
    if (!arv || arv <= 0) {
      return null;
    }

    const arvDiscount = WHOLESALE_DEFAULTS.arv_discount;
    const arvDiscounted = arv * arvDiscount;
    const repairCost = WHOLESALE_DEFAULTS.repair_tiers[repairTier];

    // Calculate assignment fee with min/max clamping
    let assignmentFee = arv * (assignmentFeePct / 100);
    assignmentFee = Math.max(
      WHOLESALE_DEFAULTS.assignment_fee_min,
      Math.min(assignmentFee, WHOLESALE_DEFAULTS.assignment_fee_max)
    );

    // Calculate MAO
    const mao = arvDiscounted - repairCost - assignmentFee;

    // Calculate spread
    const spread = askingPrice > 0 ? mao - askingPrice : null;
    const spreadPct = askingPrice > 0 && spread !== null ? spread / askingPrice : null;

    // Determine verdict
    let verdict: Verdict = 'pass';
    if (spread !== null) {
      if (spread >= WHOLESALE_DEFAULTS.verdict_thresholds.strong_buy) {
        verdict = 'strong_buy';
      } else if (spread >= WHOLESALE_DEFAULTS.verdict_thresholds.buy) {
        verdict = 'buy';
      } else if (spread >= WHOLESALE_DEFAULTS.verdict_thresholds.thin) {
        verdict = 'thin';
      }
    }

    return {
      arv,
      arvDiscount,
      arvDiscounted,
      repairTier,
      repairCost,
      assignmentFeePct: assignmentFeePct / 100,
      assignmentFee,
      mao,
      askingPrice,
      spread,
      spreadPct,
      verdict,
    };
  }, [arv, askingPrice, repairTier, assignmentFeePct]);

  return (
    <div className="bg-charcoal rounded-xl border border-slate-dark p-6">
      <div className="flex items-center gap-3 mb-6">
        <Calculator className="text-gold" size={24} />
        <h2 className="text-xl font-serif text-ivory">MAO Calculator</h2>
        <span className="text-sm text-zinc-500 bg-graphite px-2 py-1 rounded">Maximum Allowable Offer</span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left Column: Inputs */}
        <div className="space-y-5">
          {/* ARV Input */}
          <div>
            <label className="block text-sm text-stone-700 mb-2">
              After Repair Value (ARV)
              {estimatedARV && (
                <span className="ml-2 text-sm text-zinc-500">
                  (Prefilled from market estimate)
                </span>
              )}
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500">$</span>
              <input
                type="number"
                value={arv || ''}
                onChange={(e) => setArv(Number(e.target.value))}
                placeholder="Enter expected sale price after repairs"
                className="w-full bg-graphite border border-slate-dark rounded-lg pl-7 pr-4 py-3 text-ivory font-mono focus:border-gold focus:outline-none"
              />
            </div>
            <p className="text-sm text-zinc-500 mt-1">
              Expected sale price to end buyer after renovations
            </p>
          </div>

          {/* Asking Price Input */}
          <div>
            <label className="block text-sm text-stone-700 mb-2">
              Asking Price
              {initialAskingPrice && (
                <span className="ml-2 text-sm text-zinc-500">
                  (Prefilled from listing)
                </span>
              )}
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500">$</span>
              <input
                type="number"
                value={askingPrice || ''}
                onChange={(e) => setAskingPrice(Number(e.target.value))}
                placeholder="Seller's asking price"
                className="w-full bg-graphite border border-slate-dark rounded-lg pl-7 pr-4 py-3 text-ivory font-mono focus:border-gold focus:outline-none"
              />
            </div>
          </div>

          {/* EXPANSION: Repair Estimate Calculator (Phase 51-06)
              Replace fixed tiers with an itemized calculator component (RepairEstimator.tsx).
              Checklist of scope items (kitchen, bath, HVAC, roof, windows, flooring, electrical, plumbing)
              with per-item cost ranges from SSOT. Feed total into repair_cost_override.
              See: docs/architecture/WHOLESALE_SYSTEM.md § Expansion Points */}

          {/* Repair Tier Selector */}
          <div>
            <label className="block text-sm text-stone-700 mb-3">Repair Estimate</label>
            <div className="flex gap-3">
              <RepairTierButton
                tier="cosmetic"
                selected={repairTier === 'cosmetic'}
                onClick={() => setRepairTier('cosmetic')}
                cost={WHOLESALE_DEFAULTS.repair_tiers.cosmetic}
                icon={<Hammer size={18} className={repairTier === 'cosmetic' ? 'text-gold' : 'text-zinc-500'} />}
                label="Cosmetic"
                description="Paint, carpet, fixtures"
              />
              <RepairTierButton
                tier="moderate"
                selected={repairTier === 'moderate'}
                onClick={() => setRepairTier('moderate')}
                cost={WHOLESALE_DEFAULTS.repair_tiers.moderate}
                icon={<Wrench size={18} className={repairTier === 'moderate' ? 'text-gold' : 'text-zinc-500'} />}
                label="Moderate"
                description="Kitchen, bath, floors"
              />
              <RepairTierButton
                tier="gut"
                selected={repairTier === 'gut'}
                onClick={() => setRepairTier('gut')}
                cost={WHOLESALE_DEFAULTS.repair_tiers.gut}
                icon={<HardHat size={18} className={repairTier === 'gut' ? 'text-gold' : 'text-zinc-500'} />}
                label="Gut Rehab"
                description="Full renovation"
              />
            </div>
          </div>

          {/* Assignment Fee Slider */}
          <div>
            <label className="block text-sm text-stone-700 mb-2">
              Assignment Fee: <span className="text-gold font-mono">{assignmentFeePct.toFixed(1)}%</span>
              {maoResult && (
                <span className="ml-2 text-zinc-500">
                  ({formatCurrency(maoResult.assignmentFee)})
                </span>
              )}
            </label>
            <input
              type="range"
              min="3"
              max="10"
              step="0.5"
              value={assignmentFeePct}
              onChange={(e) => setAssignmentFeePct(Number(e.target.value))}
              className="w-full h-2 bg-graphite rounded-lg appearance-none cursor-pointer accent-gold"
            />
            <div className="flex justify-between text-sm text-zinc-500 mt-1">
              <span>3%</span>
              <span>10%</span>
            </div>
            <p className="text-sm text-zinc-500 mt-1">
              Your fee for assigning the contract (clamped {formatCurrency(WHOLESALE_DEFAULTS.assignment_fee_min)} - {formatCurrency(WHOLESALE_DEFAULTS.assignment_fee_max)})
            </p>
          </div>
        </div>

        {/* Right Column: Results */}
        <div className="space-y-5">
          {maoResult ? (
            <>
              {/* MAO Formula Breakdown */}
              <div className="bg-graphite rounded-lg p-5">
                <h3 className="text-sm text-stone-700 mb-4">MAO Formula</h3>
                <div className="space-y-3 font-mono text-sm">
                  <div className="flex justify-between">
                    <span className="text-stone-700">ARV</span>
                    <span className="text-ivory">{formatCurrency(maoResult.arv)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-stone-700">× 70% Rule</span>
                    <span className="text-ivory">{formatCurrency(maoResult.arvDiscounted)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-stone-700">- Repairs ({maoResult.repairTier})</span>
                    <span className="text-red-400">-{formatCurrency(maoResult.repairCost)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-stone-700">- Assignment Fee</span>
                    <span className="text-red-400">-{formatCurrency(maoResult.assignmentFee)}</span>
                  </div>
                  <hr className="border-slate-dark" />
                  <div className="flex justify-between text-lg">
                    <span className="text-gold font-semibold">MAO</span>
                    <span className="text-gold font-bold">{formatCurrency(maoResult.mao)}</span>
                  </div>
                </div>
              </div>

              {/* Spread Analysis */}
              {maoResult.spread !== null && askingPrice > 0 ? (
                <div className="space-y-4">
                  <VerdictBadge verdict={maoResult.verdict} spread={maoResult.spread} />

                  <div className="bg-graphite rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-sm text-stone-700">Your Max Offer</div>
                        <div className="text-2xl font-mono text-ivory">{formatCurrency(maoResult.mao)}</div>
                      </div>
                      <div className="text-center px-4">
                        {maoResult.spread >= 0 ? (
                          <TrendingUp className="text-green-400 mx-auto" size={24} />
                        ) : (
                          <TrendingDown className="text-red-400 mx-auto" size={24} />
                        )}
                        <div className={`text-sm font-mono ${maoResult.spread >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                          {maoResult.spread >= 0 ? '+' : ''}{formatCurrency(maoResult.spread)}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-sm text-stone-700">Asking Price</div>
                        <div className="text-2xl font-mono text-ivory">{formatCurrency(askingPrice)}</div>
                      </div>
                    </div>
                  </div>

                  {maoResult.spreadPct !== null && (
                    <div className="text-center text-sm text-stone-700">
                      Spread represents{' '}
                      <span className={maoResult.spreadPct >= 0 ? 'text-green-400' : 'text-red-400'}>
                        {(maoResult.spreadPct * 100).toFixed(1)}%
                      </span>
                      {' '}of asking price
                    </div>
                  )}
                </div>
              ) : (
                <div className="bg-graphite rounded-lg p-6 text-center">
                  <AlertCircle className="text-zinc-500 mx-auto mb-2" size={24} />
                  <p className="text-stone-700">Enter asking price to see spread analysis</p>
                </div>
              )}
            </>
          ) : (
            <div className="bg-graphite rounded-lg p-8 text-center">
              <Calculator className="text-zinc-600 mx-auto mb-3" size={32} />
              <p className="text-stone-700">Enter ARV to calculate MAO</p>
              <p className="text-sm text-zinc-500 mt-2">
                ARV = Expected sale price after renovations
              </p>
            </div>
          )}
        </div>
      </div>

      {/* EXPANSION: Rehab Scope Wizard (Future Enhancement)
          Multi-step wizard that generates an itemized renovation budget with line items.
          Pulls scope from property condition assessment and exports to PDF/CSV for contractor bids.
          Integrates with the repair estimate calculator above.
          See: docs/architecture/WHOLESALE_SYSTEM.md § Expansion Points */}

      {/* Formula Explanation */}
      <div className="mt-6 pt-4 border-t border-slate-dark">
        <p className="text-sm text-zinc-500">
          <strong className="text-stone-700">70% Rule:</strong>{' '}
          MAO = ARV × 70% - Repairs - Assignment Fee.
          The 70% ensures your end buyer has room for profit after holding costs and their own margin.
        </p>
      </div>
    </div>
  );
};

export default WholesalePanel;
